import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import { Birthday } from '@/services/birthday-services'
import { useReportsTranslation } from '@/i18n/hooks'

interface BirthdayTableProps {
  birthdays: Birthday[]
  loading: boolean
  resetPageKey?: string
}

function BirthdayTable({ birthdays, loading, resetPageKey }: BirthdayTableProps) {
  const { t } = useReportsTranslation()
  const columnHelper = createColumnHelper<Birthday>()

  const columns: ColumnDef<Birthday, any>[] = [
    columnHelper.accessor('lastName', {
      header: t('birthdays.table.lastName'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('firstName', {
      header: t('birthdays.table.firstName'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('dateOfBirth', {
      header: t('birthdays.table.dateOfBirth'),
      cell: (info) => {
        const date = new Date(info.getValue())
        return date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      },
    }),
    columnHelper.accessor('age', {
      header: t('birthdays.table.age'),
      cell: (info) => {
        const row = info.row.original
        const age = info.getValue()

        // Build highlight based on flags
        if (row.isRoundBirthday && row.isSenior) {
          // Special case: round birthday for senior (70, 80, 90, etc.)
          return (
            <span
              className="inline-flex items-center rounded-full bg-gradient-to-r from-yellow-100 to-purple-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:from-yellow-900/20 dark:to-purple-900/20 dark:text-yellow-400"
              title={`${t('birthdays.table.roundBirthdayLabel')} • ${t('birthdays.table.seniorLabel')}`}
            >
              🎉👑 {age}
            </span>
          )
        } else if (row.isRoundBirthday) {
          return (
            <span
              className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
              title={t('birthdays.table.roundBirthdayLabel')}
            >
              🎉 {age}
            </span>
          )
        } else if (row.isSpecialAge) {
          // Special age threshold (e.g., 75)
          return (
            <span
              className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-400"
              title={t('birthdays.table.specialAgeLabel')}
            >
              🌟 {age}
            </span>
          )
        } else if (row.isSenior) {
          return (
            <span
              className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
              title={t('birthdays.table.seniorLabel')}
            >
              👑 {age}
            </span>
          )
        }

        return age
      },
    }),
  ]

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-sm text-gray-500">{t('birthdays.loading')}</div>
      </div>
    )
  }

  if (birthdays.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">{t('birthdays.table.noBirthdays')}</div>
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={birthdays}
      resetPageKey={resetPageKey}
      data-testid="birthdays-table"
    />
  )
}

export default BirthdayTable